import { useCallback, useEffect, useMemo, useState } from 'react'

import { useGetPastAppointments } from './useGetPastAppointments'
import { useGetViewableDocuments } from './useGetViewableDocuments'
import groupDocumentsByEncounter from './groupDocumentsByEncounter'
import { dedupPastAppointmentsAcrossPages } from './getPastAppointmentsNextPageParam'

const collectEncounterIds = pages => {
  if (!Array.isArray(pages)) {
    return []
  }
  const ids = new Set()
  pages.forEach(page => {
    const appointments = page?.appointments ?? []
    appointments.forEach(appointment => {
      if (appointment?.id) {
        ids.add(appointment.id)
      }
    })
  })
  return Array.from(ids)
}

export const useGetPastAppointmentsWithDocuments = ({
  patientId,
  token,
  dependentAccount = null,
  isDocumentAvailable = false
}) => {
  const pastQuery = useGetPastAppointments({
    patientId,
    token,
    dependentAccount
  })

  const encounterIds = useMemo(
    () => collectEncounterIds(pastQuery.data?.pages),
    [pastQuery.data]
  )

  const documentsQuery = useGetViewableDocuments({
    patientId,
    dependentAccount,
    encounterIds,
    isAllowed: Boolean(isDocumentAvailable),
    token
  })

  const [resolvedEncounterIds, setResolvedEncounterIds] = useState(
    () => new Set()
  )
  const [hasEverResolved, setHasEverResolved] = useState(false)

  useEffect(() => {
    if (!isDocumentAvailable) {
      setResolvedEncounterIds(prev => {
        const next = new Set(encounterIds)
        if (prev.size === next.size && [...next].every(id => prev.has(id))) {
          return prev
        }
        return next
      })
      return
    }
    if (documentsQuery.dataUpdatedAt) {
      setResolvedEncounterIds(prev => {
        const next = new Set(encounterIds)
        if (prev.size === next.size && [...next].every(id => prev.has(id))) {
          return prev
        }
        return next
      })
      setHasEverResolved(true)
      return
    }
    if (
      documentsQuery.isError &&
      documentsQuery.fetchStatus === 'idle' &&
      hasEverResolved
    ) {
      setResolvedEncounterIds(prev => {
        const next = new Set(encounterIds)
        if (prev.size === next.size && [...next].every(id => prev.has(id))) {
          return prev
        }
        return next
      })
    }
  }, [
    isDocumentAvailable,
    documentsQuery.dataUpdatedAt,
    documentsQuery.isError,
    documentsQuery.fetchStatus,
    encounterIds,
    hasEverResolved
  ])

  const documentsByEncounter = useMemo(
    () => groupDocumentsByEncounter(documentsQuery.allEntries),
    [documentsQuery.allEntries]
  )

  // Track how many pages of past appointments have been "committed" — i.e.
  // both the past query AND the documents query settled idle without error.
  // On a load-more retry, React Query appends the new page to pastQuery.data
  // the moment the past fetch resolves, independently of whether the docs
  // fetch also succeeded. Without this gate, a partial-success retry
  // (past ok, docs fail) would leak the new appointments into the visible
  // list while still surfacing the retry button. We only advance the
  // committed length when both queries have settled together.
  const [committedPagesLength, setCommittedPagesLength] = useState(0)

  useEffect(() => {
    if (
      pastQuery.data &&
      pastQuery.fetchStatus === 'idle' &&
      !pastQuery.isError &&
      !documentsQuery.isError &&
      documentsQuery.fetchStatus === 'idle'
    ) {
      const currentLength = pastQuery.data.pages.length
      setCommittedPagesLength(prev =>
        currentLength > prev ? currentLength : prev
      )
    }
  }, [
    pastQuery.data,
    pastQuery.fetchStatus,
    pastQuery.isError,
    documentsQuery.isError,
    documentsQuery.fetchStatus
  ])

  // Accumulate docs from any successful docs-query state so we can fall
  // back to them when a load-more encounter-set change causes the docs
  // query to be in pending/error. During that window documentsByEncounter
  // is empty for the new key (React Query has no data yet for it), so
  // committed appointments would otherwise lose their documents.
  const [lastGoodDocumentsByEncounter, setLastGoodDocumentsByEncounter] =
    useState({})

  useEffect(() => {
    if (
      documentsQuery.data &&
      !documentsQuery.isError &&
      Object.keys(documentsByEncounter).length > 0
    ) {
      setLastGoodDocumentsByEncounter(prev => {
        const next = { ...prev, ...documentsByEncounter }
        if (Object.keys(next).length === Object.keys(prev).length) {
          return prev
        }
        return next
      })
    }
  }, [documentsQuery.data, documentsQuery.isError, documentsByEncounter])

  const effectiveDocumentsByEncounter =
    !documentsQuery.data || documentsQuery.isError
      ? lastGoodDocumentsByEncounter
      : documentsByEncounter

  const data = useMemo(() => {
    if (!pastQuery.data) {
      return undefined
    }
    const visiblePages = pastQuery.data.pages.slice(0, committedPagesLength)
    const pages = visiblePages.map(page => ({
      ...page,
      appointments: page.appointments.map(appointment => ({
        ...appointment,
        documents: effectiveDocumentsByEncounter[appointment.id] ?? []
      }))
    }))
    const flatAppointments = dedupPastAppointmentsAcrossPages(pages)
    const visibleFlatAppointments = flatAppointments.filter(apt =>
      resolvedEncounterIds.has(apt.id)
    )
    return {
      ...pastQuery.data,
      pages,
      flatAppointments: visibleFlatAppointments
    }
  }, [
    pastQuery.data,
    effectiveDocumentsByEncounter,
    resolvedEncounterIds,
    committedPagesLength
  ])

  const docsSettledOnError =
    documentsQuery.isError && documentsQuery.fetchStatus === 'idle'

  // Stop reporting `isLoading` the moment pastQuery itself has settled to
  // isError. Otherwise the spinner keeps re-arming on each retry cycle
  // (the second clause can stay true while the past hook's own retries
  // are still in flight and the documents gate hasn't resolved yet).
  const isLoading =
    (pastQuery.isLoading && !pastQuery.isError) ||
    (Boolean(isDocumentAvailable) &&
      encounterIds.length > 0 &&
      resolvedEncounterIds.size === 0 &&
      !docsSettledOnError &&
      !pastQuery.isError)

  const isFetching = pastQuery.isFetching
  const isError = pastQuery.isError || documentsQuery.isError
  const error = pastQuery.error || documentsQuery.error

  const isGateReady = useMemo(
    () =>
      !isDocumentAvailable || resolvedEncounterIds.size === encounterIds.length,
    [isDocumentAvailable, resolvedEncounterIds, encounterIds]
  )

  const [isRetrying, setIsRetrying] = useState(false)

  const retryBoth = useCallback(async () => {
    setIsRetrying(true)
    try {
      const tasks = []
      if (pastQuery.isError) {
        // { retry: false } bypasses APPOINTMENTS_RETRY so a user-initiated
        // click fires exactly one fresh request per query, not another
        // 4-attempt retry chain.
        tasks.push(
          pastQuery.hasNextPage
            ? pastQuery.fetchNextPage({}, { retry: false })
            : pastQuery.refetch({}, { retry: false })
        )
      } else if (pastQuery.hasNextPage) {
        tasks.push(pastQuery.fetchNextPage({}, { retry: false }))
      }
      if (documentsQuery.isError) {
        tasks.push(
          documentsQuery.hasNextPage
            ? documentsQuery.fetchNextPage({}, { retry: false })
            : documentsQuery.refetch({}, { retry: false })
        )
      }
      await Promise.all(tasks)
    } finally {
      setIsRetrying(false)
    }
  }, [pastQuery, documentsQuery])

  return {
    ...pastQuery,
    data,
    isLoading,
    isFetching,
    isFetchingDocs: documentsQuery.isFetching,
    isGateReady,
    isError,
    error,
    documentsQuery,
    retryBoth,
    isRetrying
  }
}
